import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { DeleteLeagueTeam, GetLeagueTeams, PutLeagueTeams } from "api/rpc/2024-04/facilityAdmin/league/scoring/teams";

import { showError, showSuccess } from "redux/actions/ui";
import { TLeagueTeam } from "redux/reducers/models/league";
import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { unsavedChangesExist } from "helpers/Helpers";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";

export default function LeagueTeamsEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { leagueId, divisionId, teamId } = useParams<{ leagueId: string; divisionId: string; teamId: string }>();

  const dispatch = useAppDispatch();

  const { state: deleteModal, updateModal, closeModal } = useModal();

  const [teamState, setTeamState] = useState<Partial<TLeagueTeam>>({ name: "" });

  // unsavedChanges bar toggle
  const [preserved, setPreserved] = useState<Partial<TLeagueTeam>>({ name: "" });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadLeagueTeams(divisionId, source.token);
    return () => source.cancel();
  }, [divisionId]);

  async function loadLeagueTeams(leagueDivisionId: string | number, token?: CancelToken) {
    const teamRes = await GetLeagueTeams({ league_division_id: leagueDivisionId, id: teamId }, true, token);
    if (token && token.reason) {
      return;
    }

    if (teamRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting team")));
      history.push(`/admin/league/${leagueId}/divisions/${divisionId}/teams`);
      return;
    }

    setTeamState(prevState => ({ ...prevState, name: teamRes.data[0]?.name }));
    setPreserved(prev => ({ ...prev, name: teamRes.data[0]?.name }));
  }

  async function updateTeam() {
    const updateRes = await PutLeagueTeams(
      { id: Number(teamId), league_division_id: divisionId, name: teamState.name },
      true,
    );
    if (updateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating team")));
      return;
    }
    dispatch(showSuccess(t("Succesfully updated team")));
    history.push(`/admin/league/${leagueId}/divisions/${divisionId}/teams`);
  }

  async function deleteTeam() {
    const res = await DeleteLeagueTeam({ id: teamId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing team from division")));
      return;
    }

    dispatch(showSuccess(t("Succesfully removed team from division")));
    history.push(`/admin/league/${leagueId}/divisions/${divisionId}/teams`);
  }

  return (
    <Page
      title={t("Edit Team")}
      narrow
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Delete"),
              handler: () => updateModal({ isOpen: true }),
              // icon: "print",
            },
            {
              type: "handler",
              label: t("Manage Players"),
              handler: () => history.push(`/admin/league/${leagueId}/divisions/${divisionId}/teams/manage_players`),
              // icon: "file-arrow-down",
            },
          ],
        },
      }}
      notificationBarProps={{
        isVisible: unsavedChangesExist(teamState, preserved),
        onAction: () => updateTeam(),
        onCancel: () => setTeamState(preserved),
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Teams"),
          url: `/admin/league/${leagueId}/divisions/${divisionId}/teams`,
        },
      ]}
    >
      <Form>
        <Card title={t("Team Details")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={teamState.name ?? ""}
                  label={t("secure.facility.league.new.012")}
                  id="name"
                  onChange={e => setTeamState(prevState => ({ ...prevState, name: e.target.value }))}
                  placeholder={t("secure.facility.league.new.012")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      </Form>

      <Portal isMounted={deleteModal.isOpen}>
        <Popup
          open={deleteModal.isOpen}
          type="warning"
          title={t("Delete Team?")}
          description={t("Are you sure you want to delete this team from the division?")}
          onCancel={closeModal}
          onOk={() => deleteTeam()}
          okText={t("Delete")}
        />
      </Portal>
    </Page>
  );
}
